import posthog from 'posthog-js';

/**
 * Mesure d'usage produit (PostHog).
 *
 * Trois contraintes propres à ce configurateur ont dicté cette implémentation :
 *
 * 1. **Il tourne en IFRAME TIERCE** sur les storefronts. Les cookies tiers sont
 *    bloqués (Safari ITP, Chrome) : la persistance passe donc par le
 *    localStorage, sinon chaque événement arrive avec un visiteur différent et
 *    aucun entonnoir n'est reconstituable.
 * 2. **Il est multi-boutique.** Sans regroupement par site, les métriques de
 *    toutes les boutiques se mélangent. D'où le `group('site', …)` et les
 *    propriétés communes posées une fois pour toutes.
 * 3. **Il ne doit jamais casser à cause de la mesure.** Toute la surface
 *    publique est encapsulée et sans effet si la clé n'est pas configurée —
 *    c'est aussi ce qui garde les développements locaux hors des statistiques.
 *
 * Configuration (runtime, cf. `env.js` / `start.sh`) :
 *   VITE_POSTHOG_KEY   — surcharge la clé ; la valeur `off` DÉSACTIVE la mesure
 *   VITE_POSTHOG_HOST  — défaut https://eu.i.posthog.com
 *
 * Sans variable, la clé par défaut ci-dessous s'applique — sauf en local, exclu
 * d'office : le déploiement n'a donc rien à configurer, et les sessions de
 * développement ne faussent pas les taux d'adoption.
 */

/** Clé projet par défaut (PXLDesigner, région EU). */
const DEFAULT_KEY = 'phc_nogT63RS5pk8apyXqMaGZYtSe9pEY9GsEfh8GR7q6ax7';
const DEFAULT_HOST = 'https://eu.i.posthog.com';

type EnvBag = Record<string, unknown> | undefined;

function readEnv(name: string): string {
    if (typeof window === 'undefined') {
        return '';
    }
    const env = (window as unknown as { env?: EnvBag }).env;
    const value = env?.[name];
    return typeof value === 'string' ? value.trim() : '';
}

function isLocalHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h.endsWith('.local');
}

let initialized = false;
let enabled = false;

/** Propriétés jointes à CHAQUE événement (contexte de la session). */
const superProperties: Record<string, unknown> = {};

export function isPostHogEnabled(): boolean {
    return enabled;
}

/**
 * Initialise PostHog une seule fois. Appelé par le routeur (cf. router/index.ts)
 * pour que la capture des vues de page couvre toute la navigation de l'app.
 */
export function usePostHog() {
    if (initialized || typeof window === 'undefined') {
        return { posthog, enabled };
    }
    initialized = true;

    const configuredKey = readEnv('VITE_POSTHOG_KEY');
    const host = readEnv('VITE_POSTHOG_HOST') || DEFAULT_HOST;

    // `off` : levier explicite pour éteindre la mesure sur un environnement
    // (préproduction, démo client) sans toucher au code.
    if (configuredKey.toLowerCase() === 'off') {
        return { posthog, enabled: false };
    }
    // Local exclu par défaut — sinon chaque session de développement gonfle les
    // taux d'adoption. Une clé posée explicitement force la mesure (tests).
    if (!configuredKey && isLocalHost()) {
        return { posthog, enabled: false };
    }

    const key = configuredKey || DEFAULT_KEY;

    try {
        posthog.init(key, {
            api_host: host,
            defaults: '2026-05-30',
            // Cookie tiers bloqué en iframe → localStorage, sinon le visiteur
            // change à chaque chargement et les entonnoirs sont inexploitables.
            persistence: 'localStorage+cookie',
            // Le configurateur est un canvas : l'autocapture des clics ne dit
            // rien d'exploitable (« clic sur div »). Les événements métier
            // explicites (cf. `trackEvent`) sont la source utile.
            autocapture: false
        });
        enabled = true;

        const embedded = window.parent !== window;
        registerAnalyticsContext({
            embedded,
            app: 'pxldesigner-configurator'
        });
    } catch (e) {
        // Bloqueur de pub, quota localStorage saturé, réseau coupé : la mesure
        // s'éteint, l'app continue.
        console.warn('[posthog] initialisation impossible', e);
        enabled = false;
    }

    return { posthog, enabled };
}

/**
 * Ajoute des propriétés au contexte de session, rejouées sur tous les
 * événements suivants (site, produit, mode d'affichage, méthode d'impression…).
 */
export function registerAnalyticsContext(properties: Record<string, unknown>) {
    Object.assign(superProperties, properties);
    if (!enabled) {
        return;
    }
    try {
        posthog.register(properties);
    } catch {
        /* la mesure ne doit jamais interrompre le parcours */
    }
}

/**
 * Rattache la session à une boutique. PostHog agrège alors nativement les
 * métriques par site, ce qui est la question qu'on se pose en multi-tenant
 * (« quelle boutique utilise quelle fonction ? »).
 */
export function identifySite(siteId: string, properties?: Record<string, unknown>) {
    if (!enabled || !siteId) {
        return;
    }
    try {
        posthog.group('site', siteId, properties);
    } catch {
        /* idem */
    }
}

/**
 * Émet un événement métier. Sans effet si la mesure est désactivée — les
 * appelants n'ont donc jamais à tester quoi que ce soit.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
    if (!enabled) {
        return;
    }
    try {
        posthog.capture(event, properties);
    } catch {
        /* idem */
    }
}

/**
 * Émet un événement AU PLUS UNE FOIS par session pour une clé donnée.
 *
 * Indispensable pour les fonctions déclenchées en boucle (déplacement d'un
 * calque, frappe au clavier) : sans ça, un seul utilisateur bavard écraserait
 * les taux d'adoption, qui répondent à « combien de sessions utilisent cette
 * fonction », pas « combien de fois ».
 */
const oncePerSession = new Set<string>();
export function trackOnce(event: string, properties?: Record<string, unknown>, key?: string) {
    const dedupeKey = key ?? event;
    if (oncePerSession.has(dedupeKey)) {
        return;
    }
    oncePerSession.add(dedupeKey);
    trackEvent(event, properties);
}
